import { t } from "i18next";
import { BookingType } from "../Booking-Form/typings/booking-inputs";
import { TeamMember } from "../Booking-Form/typings/team-member";
import { InactivateBookingsDto } from "./schedule.types";

export const updateSelectedAppointment = (
  appointment: number,
  selectedAppointments: number[],
  setSelectedAppointments: React.Dispatch<React.SetStateAction<number[]>>
) => {
  if (selectedAppointments.includes(appointment)) {
    setSelectedAppointments(() => selectedAppointments.filter(booking => booking !== appointment));
  } else setSelectedAppointments(prevState => [...prevState, appointment]);
};

export const getOptionLabel = (option: TeamMember) =>
  option.firstName ? option.firstName + " " + option.surname : "";

/** Minimal user row used to resolve schedule table NAME from booking `userBookedFor`. */
export type ScheduleNameLookupUser = {
  /** Auth subject / org user id; matches booking `userBookedFor`. */
  userId?: string;
  firstName?: string;
  surname?: string;
};

function teamMemberHasDisplayName(user: Pick<TeamMember, "firstName" | "surname">): boolean {
  return Boolean(user.firstName?.trim() || user.surname?.trim());
}

/**
 * Merges user directories for schedule table NAME lookup.
 * Later sources fill in a name only when the existing row has none.
 */
export function mergeUsersForScheduleNameLookup(
  sources: ReadonlyArray<ReadonlyArray<ScheduleNameLookupUser> | undefined>
): TeamMember[] {
  const byId = new Map<string, TeamMember>();
  for (const source of sources) {
    for (const user of source ?? []) {
      if (!user.userId) {
        continue;
      }
      const member: TeamMember = {
        userId: user.userId,
        firstName: user.firstName,
        surname: user.surname
      };
      const existing = byId.get(user.userId);
      if (!existing) {
        byId.set(user.userId, member);
        continue;
      }
      if (!teamMemberHasDisplayName(existing) && teamMemberHasDisplayName(member)) {
        byId.set(user.userId, member);
      }
    }
  }
  return [...byId.values()];
}

/** transform the first aid user name */
export const handleFirstAidUserName = (firstName: string | undefined) => {
  if (firstName?.includes("_firstNameForFirstAidUser")) {
    const [index, suffix] = firstName.split("_", 2);
    return t(`_${suffix}`, { index: index });
  }
};

/** handles the first aid user booking case included */
export const getOptionLabelWithIndex = (option: TeamMember) => {
  // check if first aid user booking is toggled on
  const isFirstAid = handleFirstAidUserName(option.firstName);
  if (isFirstAid) return isFirstAid;

  return option.firstName ? option.firstName + " " + option.surname : "";
};

export const handleMultiDeleteBookings = ({
  selectedSchedules,
  setDeletedBookings,
  inactivateBookings
}: {
  selectedSchedules: { id: number; bookingType: BookingType }[];
  setDeletedBookings?: (d: boolean) => void;
  inactivateBookings: (params: InactivateBookingsDto) => void;
}) => {
  const workplaceSchedules = selectedSchedules
    .filter(s => s.bookingType === BookingType.WORKPLACE)
    .map(w => w.id);
  const parkingSchedules = selectedSchedules
    .filter(s => s.bookingType === BookingType.PARKINGPLACE)
    .map(p => p.id);
  const electricchargingSchedules = selectedSchedules
    .filter(s => s.bookingType === BookingType.ELECTRICCHARGINGSTATIONPLACE)
    .map(e => e.id);
  const conferenceSchedules = selectedSchedules
    .filter(s => s.bookingType === BookingType.CONFERENCEZONE)
    .map(c => c.id);
  const vehicleSchedules = selectedSchedules
    .filter(s => s.bookingType === BookingType.VEHICLE)
    .map(v => v.id);

  let deleteBookings: InactivateBookingsDto["bookings"] = [];
  if (workplaceSchedules.length > 0)
    deleteBookings = [
      ...deleteBookings,
      { deleted: false, bookingIds: workplaceSchedules, bookingType: BookingType.WORKPLACE }
    ];
  if (parkingSchedules.length > 0)
    deleteBookings = [
      ...deleteBookings,
      { deleted: false, bookingIds: parkingSchedules, bookingType: BookingType.PARKINGPLACE }
    ];
  if (electricchargingSchedules.length > 0)
    deleteBookings = [
      ...deleteBookings,
      {
        deleted: false,
        bookingIds: electricchargingSchedules,
        bookingType: BookingType.ELECTRICCHARGINGSTATIONPLACE
      }
    ];
  if (conferenceSchedules.length > 0)
    deleteBookings = [
      ...deleteBookings,
      { deleted: false, bookingIds: conferenceSchedules, bookingType: BookingType.CONFERENCEZONE }
    ];
  if (vehicleSchedules.length > 0)
    deleteBookings = [
      ...deleteBookings,
      { deleted: false, bookingIds: vehicleSchedules, bookingType: BookingType.VEHICLE }
    ];

  if (!deleteBookings.length) return;

  setDeletedBookings?.(true);
  inactivateBookings({ bookings: deleteBookings });
};
